import { css } from '@emotion/react';
import {
  faCheck, faPlay, faStop, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type RefCallback, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { UnreachableCaseError } from 'ts-essentials';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { EditedBucketSelector } from '~/boundary/editMode/selectConfirm/editedBucketSelector.component';
import { useTranslation } from '../../../_shared/utils/hooks';
import {
  checkBoundaryIsCustom, useBoundaryGroupsSelector,
} from '../../../store/boundaryGroups/boundaryGroups.selectors';
import {
  boundarySelectEditActivate, boundarySelectEditReset, boundarySelectSetLassoOverlap, boundarySelectSetSelectType,
} from '../../../store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import {
  useBoundarySelectIsLassoLoadingSelector, useBoundarySelectModalOpenSelector,
} from '../../../store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.selectors';
import {
  BoundarySelectType, isBoundarySelectQuickEditCustomBoundary, SelectLassoOverlap,
} from '../../../store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { BoundarySelectConfirmComponent } from './boundarySelectConfirm.component';
import { getSelectBoundaryTypeOptions } from './boundarySelectConfirm.helpers';

const buttonsWrapperStyle = ({ buttonsCount }: { buttonsCount: number }) => css({
  display: 'flex',
  justifyContent: buttonsCount <= 1 ? 'flex-end' : 'space-between',
});

const bucketSelectorStyle = css({
  marginTop: 32,
});

const selectTypeToStepNumber = (selectType: BoundarySelectType) => {
  switch (selectType) {
    case BoundarySelectType.Click:
    case BoundarySelectType.HoverIdle:
      return 2;
    case BoundarySelectType.Lasso:
    case BoundarySelectType.HoverActive:
      return 3;
    default:
      throw new UnreachableCaseError(selectType);
  }
};

export const BoundarySelectConfirmContainer: FC = () => {
  const boundarySelectState = useBoundarySelectModalOpenSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const isBoundarySelectLassoLoading = useBoundarySelectIsLassoLoadingSelector();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [bucketSelectorMaxHeight, setBucketSelectorMaxHeight] = useState<number | null>(null);
  const [territorySwitcherElement, setTerritorySwitcherElement] = useState<HTMLDivElement | null>(null);

  const isStartingView = !boundarySelectState?.isActive;
  const editedBucketSelectorRef: RefCallback<HTMLDivElement> = useCallback((territorySwitcherElement) => {
    setTerritorySwitcherElement(territorySwitcherElement);
  }, []);

  useEffect(() => {
    const parentElement = territorySwitcherElement?.parentElement;

    if (parentElement) {
      const updateMaxAvailableHeight = () => {
        if (territorySwitcherElement && parentElement) {
          const { top: switcherTop } = territorySwitcherElement.getBoundingClientRect();
          const { bottom: parentBottom } = parentElement.getBoundingClientRect();
          const availableHeight = parentBottom - switcherTop;
          setBucketSelectorMaxHeight(Math.max(availableHeight, 0));
        }
        else {
          setBucketSelectorMaxHeight(null);
        }
      };

      const resizeObserver = new ResizeObserver(updateMaxAvailableHeight);
      resizeObserver.observe(parentElement);

      const mutationObserver = new MutationObserver(updateMaxAvailableHeight);
      mutationObserver.observe(parentElement, { childList: true, characterData: true, subtree: true });

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }

    return undefined;
  }, [territorySwitcherElement]);

  const onSubmit = useCallback(() => {
    if (!boundarySelectState?.selectedBoundaryInfos.length) {
      return;
    }

    boundarySelectState.onSubmit(boundarySelectState);
  }, [boundarySelectState]);

  const onResetClick = useCallback(() => {
    dispatch(boundarySelectEditReset());
  }, [dispatch]);

  const onSelectTypeChange = useCallback((selectType: BoundarySelectType) => {
    dispatch(boundarySelectSetSelectType(selectType));
  }, [dispatch]);

  const onLassoOverlapChange = useCallback((overlap: SelectLassoOverlap) => {
    dispatch(boundarySelectSetLassoOverlap(overlap));
  }, [dispatch]);

  const onStartButtonClick = useCallback(() => {
    dispatch(boundarySelectEditActivate());
  }, [dispatch]);

  const buttons = useMemo(() => {
    if (!boundarySelectState) {
      return [];
    }

    if (isStartingView) {
      return [{
        icon: faPlay,
        buttonStyle: ButtonStyle.Success,
        onClick: onStartButtonClick,
        label: t('Start'),
      }];
    }
    else {
      if (boundarySelectState.selectedBoundaryInfos.length === 0) {
        return [{
          icon: faStop,
          buttonStyle: isBoundarySelectLassoLoading ? ButtonStyle.Secondary : ButtonStyle.Danger,
          onClick: onResetClick,
          label: t('Stop'),
          isDisabled: isBoundarySelectLassoLoading,
        }, {
          icon: faCheck,
          buttonStyle: isBoundarySelectLassoLoading ? ButtonStyle.Secondary : ButtonStyle.Primary,
          label: t('Save'),
          isDisabled: true,
        }];
      }
      else {
        return [{
          icon: faTrash,
          buttonStyle: isBoundarySelectLassoLoading ? ButtonStyle.Secondary : ButtonStyle.Danger,
          onClick: onResetClick,
          label: t('Reset'),
          isDisabled: isBoundarySelectLassoLoading,
        }, {
          icon: faCheck,
          buttonStyle: isBoundarySelectLassoLoading ? ButtonStyle.Secondary : ButtonStyle.Primary,
          onClick: onSubmit,
          label: t('Save'),
          isDisabled: false,
        }];
      }
    }
  }, [boundarySelectState, isBoundarySelectLassoLoading, isStartingView,
    onSubmit, onResetClick, onStartButtonClick, t]);

  const boundaryGroup = boundaryGroups.find(group => group.id === boundarySelectState?.selectBoundaryGroupId);
  const boundaryIsCustom = boundaryGroup && checkBoundaryIsCustom(boundaryGroup);

  if (!boundarySelectState) {
    return null;
  }

  const options = getSelectBoundaryTypeOptions(t,
    boundarySelectState.allowMultipleSelection
      ? [BoundarySelectType.Click, BoundarySelectType.HoverIdle, BoundarySelectType.Lasso]
      : [BoundarySelectType.Click],
  );

  let heading: string;

  if (isStartingView) {
    if (boundaryIsCustom) {
      heading = t('Territory Selection Tool');
    }
    else {
      heading = t('Boundary Selection Tool');
    }
  }
  else {
    if (boundarySelectState.selectedBoundaryInfos.length === 0) {
      heading = t('Start Selecting Now');
    }
    else {
      heading = t('Select & Save');
    }
  }

  let selectType = boundarySelectState.selectType;

  // we don't want double "On Hover" option in the list so we only have HoverIdle on the list
  // that's why we need to map HoverActive to HoverIdle to have proper dropdown option displayed
  if (selectType === BoundarySelectType.HoverActive) {
    selectType = BoundarySelectType.HoverIdle;
  }

  const highlightedStepNumber = boundarySelectState.isActive ?
    selectTypeToStepNumber(boundarySelectState.selectType) :
    1;

  return (
    <BoundarySelectConfirmComponent
      dropdownHeading={t('Choose Selection Method')}
      isOpen
      showQuickEditSteps={isBoundarySelectQuickEditCustomBoundary(boundarySelectState)}
      highlightedStepNumber={highlightedStepNumber}
      onClose={boundarySelectState?.onClose}
      dropdownOptions={options}
      selectedOptionId={selectType}
      onDropdownSelect={onSelectTypeChange}
      radio={selectType !== BoundarySelectType.Lasso ? undefined : {
        heading: t('Include only:'),
        selectedValue: boundarySelectState.selectLassoOverlap,
        onChange: onLassoOverlapChange,
        items: [{
          label: boundaryIsCustom ? t('Full territories only') : t('Full boundaries only'),
          value: SelectLassoOverlap.Contains,
          isDisabled: isBoundarySelectLassoLoading,
        }, {
          label: boundaryIsCustom ? t('Full & partial territories') : t('Full & partial boundaries'),
          value: SelectLassoOverlap.Partial,
          isDisabled: isBoundarySelectLassoLoading,
        }],
      }}
      buttonsWrapperStyle={buttonsWrapperStyle({ buttonsCount: buttons.length })}
      buttons={buttons}
      heading={heading}
    >
      {isBoundarySelectQuickEditCustomBoundary(boundarySelectState) && (
        <EditedBucketSelector
          ref={editedBucketSelectorRef}
          css={bucketSelectorStyle}
          style={{
            maxHeight: (bucketSelectorMaxHeight === null) ? undefined : `${bucketSelectorMaxHeight}px`,
          }}
          buckets={boundarySelectState.selectionModeData.buckets}
          selectedBucketId={boundarySelectState.selectionModeData.selectedBucketId}
        />
      )}
    </BoundarySelectConfirmComponent>
  );
};
